import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Phone, Euro, Calendar, Filter, AlertCircle, Users, MapPin, Menu, X, RefreshCw, Send, CheckCircle, AlertTriangle, Loader } from 'lucide-react';
import LeafletCallTrackingMap from './LeafletCallTrackingMap';
import FunnelSection from './shared/FunnelSection';

// Shared utilities
import {
  formatCurrency,
  formatNumber,
  categorizePerformance,
  extractLocation,
  extractCustomerCode
} from '../utils/dashboardUtils';
import {
  getDateRange,
  getPreviousPeriodRange,
  getPeriodLabel,
  convertAmsterdamToUTC,
  getTrendDateRange,
  getPreviousTrendDateRange,
  getGranularityForPeriod
} from '../utils/dateUtils';
import { isLeadClassificationForAdmin } from '../constants/businessLogic';
import { dashboardCache, trendCache } from '../utils/performanceCache';

// Custom hooks
import { useAnalytics } from '../hooks/useAnalytics';
import { useAuth } from '../hooks/useAuth';
import { useKPIComparisons } from '../hooks/useKPIComparisons';
import { useDarkMode } from '../hooks/useDarkMode';

// Shared components
import AuthScreen from '../components/shared/AuthScreen';
import KPICard from '../components/shared/KPICard';
import { DashboardLinks } from '../components/shared/DashboardNav';
import ChartToggle from '../components/shared/ChartToggle';
import NicheSummaryCards from '../components/shared/NicheSummaryCards';
import {
  DailyCallsChart,
  CumulativeChart,
  RevenueChart,
  RevenueByNicheChart,
  ClassificationsChart
} from '../components/shared/ChartComponents';

// Data helpers (backend-API)
import { loadCustomerPrices, getPriceForDate, loadCustomerTargets, loadCallsData } from '../utils/supabaseHelpers';
import { loadFunnelData, loadTopPages, loadMatchMethodBreakdown, loadTopLandingPages, loadSourceAttribution } from '../utils/trackingHelpers';

const AdminDashboard = () => {
  const auth = useAuth('admin');
  const [isDark, setIsDark] = useDarkMode();

  const [data, setData] = useState({
    calls: [],
    customerTargets: [],
    previousCalls: [],
    trendCalls: [],  // Extended date range for trend charts
    previousTrendCalls: [],  // Previous period for trend comparison
    priceMap: null,
    loading: true,
    trendLoading: false,  // Separate loading state for trend data
    error: null,
    _performance: null
  });

  const [filters, setFilters] = useState({
    period: 'today',
    niche: 'all',
    customer: 'all',
    startDate: null,
    endDate: null,
    customPeriod: false
  });

  const [viewOptions, setViewOptions] = useState({
    cumulativeView: false
  });

  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [showAllLocations, setShowAllLocations] = useState(false);

  // Funnel state (visitor_sessions + phone_clicks)
  const [funnelState, setFunnelState] = useState({
    data: null,
    topPages: [],
    matchBreakdown: null,
    topLandingPages: [],
    sourceAttribution: [],
    loading: false,
  });

  // Webhook state
  const [webhookState, setWebhookState] = useState({
    showConfirm: false,
    loading: false,
    response: null,  // { success, status, body, timestamp }
  });

  const handleWebhookTrigger = useCallback(async () => {
    setWebhookState(prev => ({ ...prev, showConfirm: false, loading: true, response: null }));
    try {
      const res = await fetch('https://app.cookies.nl/webhook/create-dashboards', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const timestamp = new Date().toLocaleString('nl-NL', { timeZone: 'Europe/Amsterdam' });
      let body;
      try { body = await res.text(); } catch { body = ''; }
      localStorage.setItem('lm_webhook_last_trigger', timestamp);
      setWebhookState({
        showConfirm: false,
        loading: false,
        response: { success: res.ok, status: res.status, body, timestamp },
      });
    } catch (error) {
      const timestamp = new Date().toLocaleString('nl-NL', { timeZone: 'Europe/Amsterdam' });
      setWebhookState({
        showConfirm: false,
        loading: false,
        response: { success: false, status: 0, body: error.message, timestamp },
      });
    }
  }, []);

  // Analytics processing
  const analytics = useAnalytics(data.calls, data.previousCalls, data.customerTargets, viewOptions, true, data.priceMap, filters.period, data.trendCalls, data.previousTrendCalls);
  const comparisons = useKPIComparisons(analytics, analytics.previousPeriod);

  const extractCustomerCodeFromCampaign = extractCustomerCode;

  // Memoized customer targets lookup map
  const customerTargetsMap = useMemo(() => {
    const map = new Map();
    for (const target of data.customerTargets) {
      map.set(target.customer_code, target.price || 0);
    }
    return map;
  }, [data.customerTargets]);

  // Memoized helper: get price per code
  const getPriceForCode = useCallback((code, callDate = null) => {
    if (!code) return 0;
    if (data.priceMap && callDate) {
      const dateBasedPrice = getPriceForDate(data.priceMap, code, callDate);
      if (dateBasedPrice !== null) return dateBasedPrice;
    }
    return customerTargetsMap.get(code) || 0;
  }, [customerTargetsMap, data.priceMap]);

  // Memoized locations calculation
  const locations = useMemo(() => {
    if (!data.calls || data.calls.length === 0) return [];

    const locationStats = {};
    for (const call of data.calls) {
      const campaignName = call.campaign || '';
      const classification = call.classification || '';
      const location = extractLocation(campaignName);
      if (!locationStats[location]) {
        // unique = COUNT(DISTINCT (campaign, from_number)) per locatie per filter-periode
        locationStats[location] = { location, calls: 0, unique: 0, uniqueSet: new Set(), leads: 0, investment: 0 };
      }

      locationStats[location].calls++;
      const fromKey = call.from && call.from !== 'anonymous'
        ? call.from
        : (call.from === 'anonymous' ? `anonymous:${call.start}` : null);
      if (fromKey && campaignName) {
        locationStats[location].uniqueSet.add(`${campaignName}|${fromKey}`);
      }

      const isLead = isLeadClassificationForAdmin(classification, call.is_recurring);
      if (isLead) {
        locationStats[location].leads++;
        const specificCode = extractCustomerCodeFromCampaign(campaignName);
        const price = getPriceForCode(specificCode, call.start);
        if (price > 0) locationStats[location].investment += price;
      }
    }

    return Object.values(locationStats).map(loc => ({
      ...loc,
      unique: loc.uniqueSet.size,
      uniqueSet: undefined,
    })).sort((a, b) => b.investment - a.investment);
  }, [data.calls, extractCustomerCodeFromCampaign, getPriceForCode]);

  // Memoized customer options
  const customerOptions = useMemo(() => {
    return data.customerTargets
      .sort((a, b) => a.customer_code.localeCompare(b.customer_code))
      .map(target => ({
        code: target.customer_code,
        name: target.name
      }));
  }, [data.customerTargets]);

  // Filter handlers
  const handlePeriodChange = useCallback((e) => {
    const newPeriod = e.target.value;
    setFilters(prev => ({
      ...prev,
      period: newPeriod,
      customPeriod: newPeriod === 'custom'
    }));
  }, []);

  const handleNicheChange = useCallback((e) => {
    setFilters(prev => ({ ...prev, niche: e.target.value }));
  }, []);

  const handleCustomerChange = useCallback((e) => {
    setFilters(prev => ({ ...prev, customer: e.target.value }));
  }, []);

  const handleToggleFilterMenu = useCallback(() => {
    setShowFilterMenu(prev => !prev);
  }, []);

  const handleViewToggle = useCallback((checked) => {
    setViewOptions(prev => ({ ...prev, cumulativeView: checked }));
  }, []);

  // Helper: fetch calls via backend-API (server doet paginatie + filtering)
  const getAllData = useCallback((startDate, endDate) => {
    return loadCallsData(startDate, endDate, {
      niche: filters.niche,
      customer: filters.customer,
    });
  }, [filters.niche, filters.customer]);

  // Load trend data separately (lazy loading with longer cache)
  const loadTrendData = useCallback(async () => {
    // Cache-key op granularity: de trend-range is identiek voor alle periodes
    // binnen dezelfde granulariteit (deze week = vorige week = laatste 7 dagen)
    const trendCacheKey = `trend-${getGranularityForPeriod(filters.period)}-${filters.niche}-${filters.customer}`;
    const cachedTrend = trendCache.get({ period: trendCacheKey });

    if (cachedTrend) {
      setData(prev => ({
        ...prev,
        trendCalls: cachedTrend.trendCalls,
        previousTrendCalls: cachedTrend.previousTrendCalls,
        trendLoading: false
      }));
      return;
    }

    setData(prev => ({ ...prev, trendLoading: true }));

    try {
      const trendRange = getTrendDateRange(filters.period);
      const previousTrendRange = getPreviousTrendDateRange(filters.period);

      // Fetch current and previous trend data in parallel
      // getDateRange/getTrendDateRange retourneren al UTC Date-objecten
      const [trendCalls, previousTrendCalls] = await Promise.all([
        getAllData(trendRange.start, trendRange.end),
        getAllData(previousTrendRange.start, previousTrendRange.end)
      ]);

      // Cache trend data with longer TTL (10 minutes via trendCache)
      trendCache.set({ period: trendCacheKey }, { trendCalls, previousTrendCalls });

      setData(prev => ({ ...prev, trendCalls, previousTrendCalls, trendLoading: false }));
    } catch (error) {
      console.error('Error loading trend data:', error);
      setData(prev => ({ ...prev, trendLoading: false }));
    }
  }, [filters.period, filters.niche, filters.customer, getAllData]);

  // Main data loading (KPI data only - fast)
  const loadData = async () => {
    try {
      setData(prev => ({ ...prev, loading: true, error: null }));

      const cached = dashboardCache.get(filters);
      if (cached) {
        setData(prev => ({
          ...cached,
          trendCalls: prev.trendCalls, // Keep existing trend data
          previousTrendCalls: prev.previousTrendCalls,
          // Trend-herladen altijd triggeren: de granulariteit kan gewijzigd zijn
          // (dag/week/maand) en de trendCache vangt onnodige fetches zelf af.
          trendLoading: true,
          _performance: { ...cached._performance, cacheUsed: true }
        }));
        return;
      }

      const performanceStart = Date.now();

      const customerTargetsKey = 'customer_targets_static';
      let customerTargets = dashboardCache.cache.get(customerTargetsKey);

      if (!customerTargets || (Date.now() - customerTargets.timestamp) > 10 * 60 * 1000) {
        const targetsData = await loadCustomerTargets();

        customerTargets = { data: targetsData || [], timestamp: Date.now() };
        dashboardCache.cache.set(customerTargetsKey, customerTargets);
      }

      const priceMapKey = 'customer_prices_static';
      let priceMapCache = dashboardCache.cache.get(priceMapKey);

      if (!priceMapCache || (Date.now() - priceMapCache.timestamp) > 10 * 60 * 1000) {
        const priceMapData = await loadCustomerPrices();
        priceMapCache = { data: priceMapData, timestamp: Date.now() };
        dashboardCache.cache.set(priceMapKey, priceMapCache);
      }

      const currentRange = getDateRange(filters.period, filters.startDate, filters.endDate);
      const previousRange = getPreviousPeriodRange(filters.period, filters.startDate, filters.endDate);

      // Only fetch KPI data (current + previous period) - trend data loaded separately
      const [currentCalls, previousCalls] = await Promise.all([
        getAllData(currentRange.start, currentRange.end),
        getAllData(previousRange.start, previousRange.end)
      ]);

      const totalTime = Date.now() - performanceStart;

      const resultData = {
        calls: currentCalls,
        previousCalls: previousCalls,
        trendCalls: [], // Will be loaded separately
        customerTargets: customerTargets.data,
        priceMap: priceMapCache.data,
        loading: false,
        trendLoading: true, // Trend data will be loaded next
        error: null,
        _performance: {
          totalTime,
          recordCount: currentCalls.length + previousCalls.length,
          currentRecords: currentCalls.length,
          previousRecords: previousCalls.length,
          cacheUsed: false
        }
      };

      dashboardCache.set(filters, resultData);
      setData(prev => ({
        ...resultData,
        trendCalls: prev.trendCalls,
        previousTrendCalls: prev.previousTrendCalls
      }));

    } catch (error) {
      console.error('Error:', error);
      setData(prev => ({ ...prev, loading: false, error: error.message }));
    }
  };

  // Load funnel data (visitor_sessions + phone_clicks) — parallel, non-blocking
  const loadFunnelSection = useCallback(async () => {
    if (!auth.authenticated) return;
    setFunnelState(prev => ({ ...prev, loading: true }));
    try {
      const currentRange = getDateRange(filters.period, filters.startDate, filters.endDate);
      const start = currentRange.start;
      const end = currentRange.end;
      const niche = filters.niche;
      const [funnelData, topPages, matchBreakdown, topLandingPages, sourceAttribution] = await Promise.all([
        loadFunnelData(start, end, niche),
        loadTopPages(start, end, niche, 10),
        loadMatchMethodBreakdown(start, end, niche),
        loadTopLandingPages(start, end, niche, 10),
        loadSourceAttribution(start, end, niche),
      ]);
      setFunnelState({ data: funnelData, topPages, matchBreakdown, topLandingPages, sourceAttribution, loading: false });
    } catch (err) {
      console.error('Error loading funnel section:', err);
      setFunnelState(prev => ({ ...prev, loading: false }));
    }
  }, [auth.authenticated, filters.period, filters.niche, filters.startDate, filters.endDate]);

  const handleManualRefresh = useCallback(() => {
    // Only clear KPI cache, keep trend cache (historical data rarely changes)
    dashboardCache.clear();
    // trendCache is NOT cleared - historical trend data is cached for 10 minutes
    setData(prev => ({ ...prev, loading: true }));
    loadData();
    loadFunnelSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, loadFunnelSection]);

  // Effects - Load KPI data
  useEffect(() => {
    if (auth.authenticated) {
      loadData();
      loadFunnelSection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.authenticated, filters.period, filters.niche, filters.customer, filters.startDate, filters.endDate]);

  // Effects - Load trend data after KPI data is ready (lazy loading).
  // Alleen op trendLoading triggeren: de oude conditie (calls.length > 0 &&
  // trendCalls.length === 0) blokkeerde het herladen bij een periode-wissel
  // via de KPI-cache (stale grafiek met verkeerde granulariteit) én bleef
  // hangen wanneer de KPI-periode 0 calls had.
  useEffect(() => {
    if (!data.loading && data.trendLoading) {
      loadTrendData();
    }
  }, [data.loading, data.trendLoading, loadTrendData]);

  // Auto-refresh effect
  useEffect(() => {
    if (!auth.authenticated || !autoRefresh) return;

    const interval = setInterval(() => {
      handleManualRefresh();
    }, 60000);

    return () => clearInterval(interval);
  }, [auth.authenticated, autoRefresh, handleManualRefresh]);

  // Auth check
  if (!auth.authenticated) {
    return (
      <AuthScreen
        auth={auth}
        title="LEAD MAGNATE"
        subtitle="Admin Dashboard"
        error={data.error}
      />
    );
  }

  // UI Sub-Components
  const PeriodSelector = ({ compact = false }) => (
    <div className="select-wrapper">
      <Calendar className="select-icon w-4 h-4" />
      <select
        value={filters.period}
        onChange={handlePeriodChange}
        className="select-custom"
        style={{ minWidth: compact ? '130px' : '150px' }}
      >
        <option value="today">Vandaag</option>
        <option value="yesterday">Gisteren</option>
        <option value="thisWeek">Deze Week</option>
        <option value="lastWeek">Vorige Week</option>
        <option value="thisMonth">Deze Maand</option>
        <option value="lastMonth">Vorige Maand</option>
        <option value="last7days">Laatste 7 Dagen</option>
        <option value="last30days">Laatste 30 Dagen</option>
        <option value="custom">Aangepast</option>
      </select>
    </div>
  );

  const CustomDatePicker = () => {
    const [tempStartDate, setTempStartDate] = useState(filters.startDate || '');
    const [tempEndDate, setTempEndDate] = useState(filters.endDate || '');

    if (!filters.customPeriod) return null;

    const handleDateChange = (type, value) => {
      if (type === 'start') {
        setTempStartDate(value);
      } else {
        setTempEndDate(value);
      }

      if (type === 'start' && value.length === 10 && tempEndDate.length === 10) {
        setFilters(prev => ({ ...prev, startDate: value, endDate: tempEndDate }));
      } else if (type === 'end' && value.length === 10 && tempStartDate.length === 10) {
        setFilters(prev => ({ ...prev, startDate: tempStartDate, endDate: value }));
      }
    };

    return (
      <div className="flex items-center gap-2">
        <input
          type="date"
          value={tempStartDate}
          onChange={(e) => handleDateChange('start', e.target.value)}
          className="input-date"
        />
        <span style={{ color: 'var(--color-neutral-400)', fontSize: 'var(--text-sm)' }}>tot</span>
        <input
          type="date"
          value={tempEndDate}
          onChange={(e) => handleDateChange('end', e.target.value)}
          className="input-date"
        />
      </div>
    );
  };

  const FilterSelector = ({ compact = false }) => (
    <div className="select-wrapper">
      <Filter className="select-icon w-4 h-4" />
      <select
        value={filters.niche}
        onChange={handleNicheChange}
        className="select-custom"
        style={{ minWidth: compact ? '130px' : '150px' }}
      >
        <option value="all">Alle Niches</option>
        <option value="ontstoppen">Ontstoppen</option>
        <option value="loodgieter">Loodgieter</option>
        <option value="dakdekker">Dakdekker</option>
      </select>
    </div>
  );

  const CustomerSelector = ({ compact = false }) => (
    <div className="select-wrapper">
      <Users className="select-icon w-4 h-4" />
      <select
        value={filters.customer}
        onChange={handleCustomerChange}
        className="select-custom"
        style={{ minWidth: compact ? '150px' : '180px' }}
      >
        <option value="all">Alle Klanten</option>
        {customerOptions.map(customer => (
          <option key={customer.code} value={customer.code}>
            {customer.code} - {customer.name}
          </option>
        ))}
      </select>
    </div>
  );

  const PerformanceIndicator = () => {
    if (!data._performance) return null;

    const { totalTime, recordCount, cacheUsed } = data._performance;
    const perfCategory = categorizePerformance(totalTime);

    return (
      <span className="font-mono" style={{
        fontSize: 'var(--text-xs)',
        color: perfCategory.color === 'green' ? 'var(--color-success)' :
               perfCategory.color === 'yellow' ? 'var(--color-warning)' :
               'var(--color-error)'
      }}>
        {cacheUsed ? (
          <>Cached &bull; {formatNumber(recordCount)} records</>
        ) : (
          <>{totalTime}ms &bull; {formatNumber(recordCount)} records</>
        )}
      </span>
    );
  };

  // Loading state
  if (data.loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p className="loading-text">Laden van analytics data...</p>
        {data._performance?.recordCount && (
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-neutral-400)', marginTop: 'var(--space-2)' }}>
            Loading {formatNumber(data._performance.recordCount)} records...
          </p>
        )}
      </div>
    );
  }

  // Error state
  if (data.error) {
    return (
      <div className="error-screen">
        <AlertCircle className="error-icon" />
        <p className="error-message">Fout bij laden van data: {data.error}</p>
        <button
          onClick={() => {
            dashboardCache.clear();
            loadData();
          }}
          className="btn btn-primary"
        >
          Cache wissen en opnieuw proberen
        </button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-neutral-100)' }}>
      {/* Mobile side panel */}
      {showFilterMenu && (
        <>
          <div
            className="side-panel-backdrop"
            onClick={() => setShowFilterMenu(false)}
          />
          <aside className="side-panel" role="dialog" aria-label="Menu">
            <button
              className="side-panel-close"
              onClick={() => setShowFilterMenu(false)}
              title="Sluiten"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="side-panel-content">
              <div className="side-panel-section">
                <div className="side-panel-section-title">Filters</div>
                <PeriodSelector compact />
                <CustomDatePicker />
                <FilterSelector compact />
                <CustomerSelector compact />
              </div>

              <div className="side-panel-section">
                <div className="side-panel-section-title">Status</div>
                <div className="side-panel-status">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <span className={`status-dot ${autoRefresh ? 'status-dot-pulse' : ''}`} />
                    <span>{new Date().toLocaleString('nl-NL', { timeZone: 'Europe/Amsterdam' })}</span>
                  </div>
                  <PerformanceIndicator />
                </div>
              </div>

              <div className="side-panel-section">
                <div className="side-panel-row">
                  <span>Auto-refresh</span>
                  <label className="auto-refresh-toggle">
                    <input
                      type="checkbox"
                      checked={autoRefresh}
                      onChange={(e) => setAutoRefresh(e.target.checked)}
                    />
                    <span className="toggle-slider" />
                  </label>
                </div>
                <div className="side-panel-row">
                  <span>Donkere modus</span>
                  <label className="auto-refresh-toggle">
                    <input
                      type="checkbox"
                      checked={isDark}
                      onChange={() => setIsDark(prev => !prev)}
                    />
                    <span className="toggle-slider" />
                  </label>
                </div>
              </div>

              <hr className="side-panel-separator" />

              <DashboardLinks active="calltracking" />

              <hr className="side-panel-separator" />

              <button
                className="side-panel-action"
                onClick={() => {
                  setShowFilterMenu(false);
                  setWebhookState(prev => ({ ...prev, showConfirm: true, response: null }));
                }}
                disabled={webhookState.loading}
              >
                {webhookState.loading
                  ? <Loader className="w-4 h-4 animate-spin" />
                  : <Send className="w-4 h-4" />}
                <span>Dashboards aanmaken</span>
              </button>
            </div>
          </aside>
        </>
      )}

      {/* Slim topbar */}
      <header className="topbar">
        <div className="topbar-inner">
          <span className="topbar-wordmark">Lead Magnate</span>
          <div className="topbar-actions">
            <button
              onClick={handleManualRefresh}
              disabled={data.loading}
              className="topbar-icon-btn"
              title={autoRefresh ? "Auto-refresh actief (60s)" : "Ververs data"}
            >
              <RefreshCw className={`w-4 h-4 ${data.loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleToggleFilterMenu}
              className="topbar-icon-btn"
              title="Menu"
            >
              <Menu className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl xl:max-w-[140rem] 2xl:max-w-[160rem] mx-auto px-3 sm:px-4 py-4 lg:py-6">
        {/* KPI Cards */}
        <div className="hero-kpi-grid grid grid-cols-2 gap-4 lg:gap-6 mb-4 lg:mb-6">
          <KPICard
            title="OMZET"
            value={formatCurrency(analytics.totalRevenue)}
            subtitle={`${formatCurrency(analytics.leadCalls > 0 ? analytics.totalRevenue / analytics.leadCalls : 0)} per lead`}
            icon={Euro}
            comparison={comparisons.revenueComparison}
            compactComparison
            className="hero-kpi"
          />
          <KPICard
            title="LEADS"
            value={formatNumber(analytics.leadCalls)}
            subtitle={`${analytics.totalCalls} calls · ${analytics.totalUnique} uniek`}
            icon={Phone}
            comparison={comparisons.leadsComparison}
            compactComparison
            className="hero-kpi"
          />
        </div>

        {/* Funnel */}
        <FunnelSection
          funnelData={funnelState.data}
          topPages={funnelState.topPages}
          matchBreakdown={funnelState.matchBreakdown}
          topLandingPages={funnelState.topLandingPages}
          sourceAttribution={funnelState.sourceAttribution}
          loading={funnelState.loading}
          calls={analytics.totalCalls}
          leads={analytics.leadCalls}
          revenue={analytics.totalRevenue}
        />

        {/* Map + Customer Performance naast elkaar op desktop (zelfde
            grid-patroon als de charts eronder); tabel scrollt binnen de
            kaart-hoogte zodat de rij gelijk blijft */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6 mb-6 lg:mb-8">
        <div style={{ borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)' }}>
          <LeafletCallTrackingMap
            callData={data.calls}
            height="600px"
            isAdmin={true}
            customerTargets={data.customerTargets || []}
            priceMap={data.priceMap}
            onLocationClick={() => {}}
            isDark={isDark}
          />
        </div>

        {/* Customer Performance Table */}
        <div className="table-container" style={{ maxHeight: '600px', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div className="table-header">
            <h3>CUSTOMER PERFORMANCE</h3>
          </div>
          <div className="overflow-x-auto" style={{ overflowY: 'auto' }}>
            <table className="table-premium">
              <thead>
                <tr>
                  <th>KLANT</th>
                  <th>OMZET</th>
                  <th>LEADS</th>
                  <th>UNIEK</th>
                  <th>CALLS</th>
                </tr>
              </thead>
              <tbody>
                {(analytics.topCustomers || []).map((customer, index) => {
                  const rankEmoji = index < 3 ? ['🥇', '🥈', '🥉'][index] : '';
                  return (
                    <tr key={index}>
                      <td className="cell-primary">
                        <span className="rank-badge">
                          {rankEmoji && <span>{rankEmoji}</span>}
                          <span>{customer.name}</span>
                        </span>
                      </td>
                      <td className="cell-success">{formatCurrency(customer.revenue)}</td>
                      <td>{formatNumber(customer.leadCalls)}</td>
                      <td>{formatNumber(customer.unique || 0)}</td>
                      <td>{formatNumber(customer.totalCalls)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        </div>

        {/* Charts Section */}
        <div className="mb-6 lg:mb-8">
          <div className="flex justify-end mb-4">
            <ChartToggle
              cumulativeView={viewOptions.cumulativeView}
              onToggle={handleViewToggle}
            />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6 mb-4 lg:mb-6">
            {viewOptions.cumulativeView ? (
              <>
                <RevenueChart data={analytics.trends || []} period={filters.period} showComparison={true} isCumulative={true} granularity={analytics.granularity} />
                <CumulativeChart data={analytics.trends || []} period={filters.period} showComparison={true} granularity={analytics.granularity} />
              </>
            ) : (
              <>
                <RevenueChart data={analytics.trends || []} period={filters.period} showComparison={false} isCumulative={false} granularity={analytics.granularity} />
                <DailyCallsChart data={analytics.trends || []} period={filters.period} showComparison={false} granularity={analytics.granularity} />
              </>
            )}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6">
            <RevenueByNicheChart data={analytics.niches || []} />
            <ClassificationsChart data={analytics.classifications || []} />
          </div>
        </div>

        {/* Niche Performance */}
        <div className="mb-6 lg:mb-8">
          <h2 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-bold)',
            color: 'var(--color-neutral-800)',
            marginBottom: 'var(--space-4)'
          }}>
            NICHE PERFORMANCE
          </h2>
          <NicheSummaryCards niches={analytics.niches || []} />
        </div>

        {/* Per Locatie Table */}
        {locations.length > 0 && (
          <div className="table-container mb-6 lg:mb-8">
            <div className="table-header">
              <h3>
                <MapPin className="w-5 h-5" style={{ color: 'var(--color-brand-600)' }} />
                PER LOCATIE
              </h3>
              {locations.length > 10 && (
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-neutral-500)' }}>
                  {showAllLocations ? locations.length : Math.min(10, locations.length)} van {locations.length} locaties
                </span>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="table-premium">
                <thead>
                  <tr>
                    <th>PLAATS</th>
                    <th>OMZET</th>
                    <th>LEADS</th>
                    <th>UNIEK</th>
                    <th>CALLS</th>
                  </tr>
                </thead>
                <tbody>
                  {(showAllLocations ? locations : locations.slice(0, 10)).map((loc, index) => (
                    <tr key={index}>
                      <td className="cell-primary">{loc.location.toUpperCase()}</td>
                      <td className="cell-success">{formatCurrency(loc.investment)}</td>
                      <td>{formatNumber(loc.leads)}</td>
                      <td>{formatNumber(loc.unique)}</td>
                      <td>{formatNumber(loc.calls)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {locations.length > 10 && (
              <div style={{ textAlign: 'center', paddingTop: 'var(--space-4)' }}>
                <button
                  onClick={() => setShowAllLocations(!showAllLocations)}
                  className="btn btn-outline btn-sm"
                >
                  {showAllLocations ? 'Toon minder' : `Toon alle ${locations.length} locaties`}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Webhook Response Toast */}
        {webhookState.response && (
          <div style={{
            position: 'fixed',
            bottom: 'var(--space-4)',
            right: 'var(--space-4)',
            maxWidth: '420px',
            zIndex: 60,
            padding: 'var(--space-3) var(--space-4)',
            borderRadius: 'var(--radius-md)',
            background: webhookState.response.success ? 'var(--color-success-light)' : 'var(--color-error-light)',
            border: `1px solid ${webhookState.response.success ? 'var(--color-success)' : 'var(--color-error)'}`,
            boxShadow: 'var(--shadow-lg)',
            display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)',
          }}>
            {webhookState.response.success ? (
              <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-success)', flexShrink: 0, marginTop: '1px' }} />
            ) : (
              <AlertTriangle className="w-5 h-5" style={{ color: 'var(--color-error)', flexShrink: 0, marginTop: '1px' }} />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{
                fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)',
                color: webhookState.response.success ? 'var(--color-success)' : 'var(--color-error)',
              }}>
                {webhookState.response.success ? 'Webhook succesvol verstuurd' : 'Webhook mislukt'}
              </p>
              <div style={{
                marginTop: 'var(--space-2)', fontSize: 'var(--text-xs)',
                fontFamily: 'var(--font-mono)', color: 'var(--color-neutral-600)',
                display: 'flex', flexDirection: 'column', gap: 'var(--space-1)',
              }}>
                <span>Status: {webhookState.response.status || 'Geen verbinding'}</span>
                <span style={{ color: 'var(--color-neutral-400)' }}>
                  {webhookState.response.timestamp}
                </span>
              </div>
            </div>
            <button
              onClick={() => setWebhookState(prev => ({ ...prev, response: null }))}
              className="btn btn-icon-subtle"
              style={{ flexShrink: 0 }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Webhook Confirmation Modal */}
        {webhookState.showConfirm && (
          <div
            onClick={() => setWebhookState(prev => ({ ...prev, showConfirm: false }))}
            style={{
              position: 'fixed', inset: 0, zIndex: 50,
              background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 'var(--space-4)',
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'var(--surface)', borderRadius: 'var(--radius-xl)',
                boxShadow: 'var(--shadow-xl)', maxWidth: '420px', width: '100%',
                overflow: 'hidden',
              }}
            >
              <div style={{
                padding: 'var(--space-5) var(--space-6)',
                borderBottom: '1px solid var(--color-neutral-200)',
                display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
              }}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: 'var(--radius-lg)',
                  background: 'var(--color-warning-light)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <AlertTriangle className="w-5 h-5" style={{ color: 'var(--color-warning)' }} />
                </div>
                <div>
                  <h3 style={{
                    fontFamily: 'var(--font-display)', fontSize: 'var(--text-base)',
                    fontWeight: 'var(--font-bold)', color: 'var(--color-neutral-900)',
                  }}>
                    Dashboards aanmaken?
                  </h3>
                  <p style={{
                    fontSize: 'var(--text-sm)', color: 'var(--color-neutral-500)',
                    marginTop: 'var(--space-1)',
                  }}>
                    Dit triggert de N8N facturatie-automatisering
                  </p>
                </div>
              </div>
              <div style={{
                padding: 'var(--space-4) var(--space-6)',
                fontSize: 'var(--text-sm)', color: 'var(--color-neutral-600)',
                lineHeight: '1.6',
              }}>
                Er wordt een POST request verstuurd naar de N8N webhook.
                De facturatie-dashboards worden dan automatisch aangemaakt.
              </div>
              <div style={{
                padding: 'var(--space-4) var(--space-6)',
                borderTop: '1px solid var(--color-neutral-100)',
                display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-3)',
              }}>
                <button
                  onClick={() => setWebhookState(prev => ({ ...prev, showConfirm: false }))}
                  className="btn btn-ghost btn-sm"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleWebhookTrigger}
                  className="btn btn-sm"
                  style={{
                    background: 'var(--color-warning)', color: 'white',
                    fontWeight: 'var(--font-semibold)',
                    display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
                  }}
                >
                  <Send className="w-4 h-4" />
                  Bevestigen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="dashboard-footer">
          <p className="footer-brand">LEAD MAGNATE</p>

          {/* Desktop Footer Meta */}
          <div className="hidden lg:flex footer-meta">
            <span className="footer-meta-item">{getPeriodLabel(filters.period)}</span>
            <span className="footer-divider">&bull;</span>
            <span className="footer-meta-item">{formatNumber((analytics.trends || []).length)} dagen</span>
            <span className="footer-divider">&bull;</span>
            <span className="footer-meta-item">{formatNumber(analytics.totalCalls)} calls</span>
            <span className="footer-divider">&bull;</span>
            <span className="footer-meta-item">{formatNumber(analytics.leadCalls)} leads</span>
            <span className="footer-divider">&bull;</span>
            <span className="footer-meta-item">{formatCurrency(analytics.totalRevenue)}</span>
            {data._performance && (
              <>
                <span className="footer-divider">&bull;</span>
                <PerformanceIndicator />
              </>
            )}
          </div>

          {/* Mobile Footer Meta */}
          <div className="lg:hidden footer-meta" style={{ flexDirection: 'column', gap: 'var(--space-2)' }}>
            <div className="flex justify-center gap-4">
              <span>{getPeriodLabel(filters.period)}</span>
              <span>&bull;</span>
              <span>{formatNumber(analytics.totalCalls)} calls</span>
            </div>
            <div className="flex justify-center gap-4">
              <span>{formatNumber(analytics.leadCalls)} leads</span>
              <span>&bull;</span>
              <span>{formatCurrency(analytics.totalRevenue)}</span>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default AdminDashboard;
